/**
 * Owner-only per-page click analytics footer (INT-0034).
 *
 * Renders a "+" toggle at the VERY BOTTOM of every admin page — mounted by
 * the shared layouts (OrganizationLayout, CreateOrganizationLayout), so new
 * pages get it for free (rule in CLAUDE.md → "Admin usage tracking").
 *
 * Contract:
 *  - Visible ONLY to the hardcoded platform owner email below. Everyone else
 *    renders nothing (and the API 404s them server-side via
 *    requirePlatformOwner — the hardcode is cosmetic gating, not security).
 *  - LAZY: no analytics API call on page load, ever. The first "+" click
 *    fetches GET /adminUsage/page-clicks?page=<pathname>; collapsing and
 *    re-expanding reuses the fetched data for the same path + scope.
 *  - Scope dropdown: "All organizations" (default) vs the currently selected
 *    organization — the org scope passes &org=<id> so only events beaconed
 *    under that org are counted.
 *  - Table: rows = organizer emails, columns = top click labels on this page
 *    (last 30 days), plus "other" and a total. Hovering a count shows the
 *    LAST event's date/time for that cell (server-supplied lastAts).
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useOrganization, useUser } from '@/auth/clerkShim';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const OWNER_EMAIL = 'taku30@gmail.com';
const ENDPOINT = `${process.env.API_URL}/api/v1/adminUsage/page-clicks`;
const RECORDINGS_ENDPOINT = `${process.env.API_URL}/api/v1/adminUsage/recordings`;

interface PageClicksData {
  page: string;
  days: number;
  labels: string[];
  rows: Array<{
    email: string;
    counts: number[];
    lastAts: Array<string | null>;
    other: number;
    otherLastAt: string | null;
    total: number;
  }>;
}

function relativeTime(d: Date): string {
  const s = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.round(h / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
}

/** Hover card for a count cell: element label, count, and latest event date. */
function CountCell({ n, label, lastAt }: { n: number; label: string; lastAt: string | null }) {
  if (!n) return <td className="border px-2 py-1 text-right" />;
  const d = lastAt ? new Date(lastAt) : null;
  const valid = d && !isNaN(d.getTime());
  if (!valid) return <td className="border px-2 py-1 text-right">{n}</td>;
  return (
    <td className="border px-2 py-1 text-right">
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-default underline decoration-dotted decoration-muted-foreground/50 underline-offset-2">
            {n}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="text-xs">
            <div className="font-medium">{label}</div>
            <div className="mt-0.5 text-muted-foreground">
              {n} click{n === 1 ? '' : 's'} · last {d!.toLocaleString()}
            </div>
            <div className="text-muted-foreground">{relativeTime(d!)}</div>
          </div>
        </TooltipContent>
      </Tooltip>
    </td>
  );
}

interface RecordingSession {
  sessionId: string;
  email: string;
  organizationId: string | null;
  startedAt: string;
  durationMs: number;
  chunkCount: number;
  eventCount: number;
}

function formatDuration(ms: number): string {
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  return m ? `${m}m ${String(s % 60).padStart(2, '0')}s` : `${s}s`;
}

export default function PageClicksFooter() {
  const { user } = useUser();
  const { organization } = useOrganization();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<'all' | 'org'>('all');
  const [data, setData] = useState<PageClicksData | null>(null);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<RecordingSession[] | null>(null);
  const [sessionsKey, setSessionsKey] = useState<string | null>(null);
  const [replaying, setReplaying] = useState<RecordingSession | null>(null);
  const [replayError, setReplayError] = useState<string | null>(null);
  const playerHost = useRef<HTMLDivElement | null>(null);

  // Route change → collapse. Keeps the lazy contract per page and never
  // shows one page's table on another.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const email = (user?.primaryEmailAddress?.emailAddress || '').toLowerCase();
  if (email !== OWNER_EMAIL) return null;

  const orgId = organization?.id ? String(organization.id) : null;

  const load = async (nextScope: 'all' | 'org') => {
    const key = `${location.pathname}|${nextScope === 'org' ? orgId : 'all'}`;
    if (loadedKey === key) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: location.pathname });
      if (nextScope === 'org' && orgId) params.set('org', orgId);
      const res = await fetch(`${ENDPOINT}?${params.toString()}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setData(body.result || body);
      setLoadedKey(key);
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const loadSessions = async (nextScope: 'all' | 'org') => {
    const key = nextScope === 'org' ? orgId || 'all' : 'all';
    if (sessionsKey === key) return;
    try {
      const params = new URLSearchParams();
      if (nextScope === 'org' && orgId) params.set('org', orgId);
      const res = await fetch(`${RECORDINGS_ENDPOINT}/sessions?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setSessions((body.result || body).sessions || []);
      setSessionsKey(key);
    } catch {
      setSessions([]); // sessions are secondary — fail quiet, keep the click table
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      void load(scope);
      void loadSessions(scope);
    }
  };

  const onScopeChange = (value: 'all' | 'org') => {
    setScope(value);
    void load(value);
    void loadSessions(value);
  };

  const play = async (session: RecordingSession) => {
    setReplaying(session);
    setReplayError(null);
    try {
      const res = await fetch(`${RECORDINGS_ENDPOINT}/session/${encodeURIComponent(session.sessionId)}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      const events = (body.result || body).events || [];
      if (events.length < 2) throw new Error('Recording too short to replay');
      // Player code (and its CSS) load only here — nobody but the owner ever
      // downloads it.
      const [{ default: RrwebPlayer }] = await Promise.all([
        import('rrweb-player'),
        // @ts-expect-error — CSS side-effect import; Vite resolves it, TS can't.
        import('rrweb-player/dist/style.css'),
      ]);
      if (!playerHost.current) return;
      playerHost.current.innerHTML = '';
      new RrwebPlayer({
        target: playerHost.current,
        props: {
          events,
          autoPlay: true,
          // Fast-forwards idle gaps so a 10-minute coffee break doesn't stall
          // the replay (the ≤20s wait contract).
          skipInactive: true,
          width: Math.min(1024, window.innerWidth - 120),
        },
      });
    } catch (e: any) {
      setReplayError(e?.message || 'Replay failed');
    }
  };

  const closeReplay = () => {
    if (playerHost.current) playerHost.current.innerHTML = '';
    setReplaying(null);
    setReplayError(null);
  };

  return (
    <div className="mt-8 border-t pt-2 pb-3 text-xs text-muted-foreground">
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-tour="page-clicks-toggle"
          aria-label="Toggle page click analytics"
          aria-expanded={open}
          onClick={toggle}
          className="flex h-6 w-6 items-center justify-center rounded border bg-card font-mono text-sm leading-none hover:bg-muted/50"
        >
          {open ? '−' : '+'}
        </button>
        {open && (
          <select
            aria-label="Click analytics scope"
            value={scope}
            onChange={(e) => onScopeChange(e.target.value as 'all' | 'org')}
            className="h-6 rounded border bg-card px-1 text-xs"
          >
            <option value="all">All organizations</option>
            {orgId && <option value="org">{organization?.name || 'Current organization'}</option>}
          </select>
        )}
      </div>
      {open && (
        <div className="mt-2 overflow-x-auto">
          {loading && <div>Loading click data…</div>}
          {error && <div className="text-destructive">Click data failed: {error}</div>}
          {!loading && !error && data && data.rows.length === 0 && (
            <div>No clicks recorded on this page in the last {data.days} days.</div>
          )}
          {!loading && !error && data && data.rows.length > 0 && (
            <TooltipProvider delayDuration={150}>
              <table className="w-max border-collapse" data-testid="page-clicks-table">
                <thead>
                  <tr>
                    <th className="border px-2 py-1 text-left font-medium">Organizer</th>
                    {data.labels.map((l) => (
                      <th key={l} className="border px-2 py-1 text-left font-medium" title={l}>
                        {l.length > 24 ? `${l.slice(0, 24)}…` : l}
                      </th>
                    ))}
                    <th className="border px-2 py-1 text-left font-medium">other</th>
                    <th className="border px-2 py-1 text-left font-medium">total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.email}>
                      <td className="border px-2 py-1">{r.email}</td>
                      {r.counts.map((n, i) => (
                        <CountCell key={i} n={n} label={data.labels[i]} lastAt={r.lastAts?.[i] ?? null} />
                      ))}
                      <CountCell n={r.other} label="other elements" lastAt={r.otherLastAt} />
                      <td className="border px-2 py-1 text-right font-medium">{r.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TooltipProvider>
          )}
          {sessions && sessions.length > 0 && (
            <div className="mt-4">
              <div className="mb-1 font-medium">Session replays (last 7 days)</div>
              <table className="w-max border-collapse" data-testid="session-replays-table">
                <thead>
                  <tr>
                    <th className="border px-2 py-1 text-left font-medium">Organizer</th>
                    <th className="border px-2 py-1 text-left font-medium">Started</th>
                    <th className="border px-2 py-1 text-left font-medium">Duration</th>
                    <th className="border px-2 py-1 text-left font-medium">Events</th>
                    <th className="border px-2 py-1 text-left font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => (
                    <tr key={s.sessionId}>
                      <td className="border px-2 py-1">{s.email}</td>
                      <td className="border px-2 py-1">{new Date(s.startedAt).toLocaleString()}</td>
                      <td className="border px-2 py-1 text-right">{formatDuration(s.durationMs)}</td>
                      <td className="border px-2 py-1 text-right">{s.eventCount}</td>
                      <td className="border px-2 py-1">
                        <button
                          type="button"
                          aria-label={`Play session from ${s.email}`}
                          onClick={() => void play(s)}
                          className="rounded border bg-card px-2 py-0.5 hover:bg-muted/50"
                        >
                          ▶ Play
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {replaying && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6">
          <div className="max-h-full overflow-auto rounded-lg bg-card p-4 shadow-xl">
            <div className="mb-2 flex items-center justify-between gap-6">
              <div className="text-sm">
                <span className="font-medium">{replaying.email}</span>
                {' · '}
                {new Date(replaying.startedAt).toLocaleString()} · {formatDuration(replaying.durationMs)}
              </div>
              <button
                type="button"
                aria-label="Close replay"
                onClick={closeReplay}
                className="rounded border bg-card px-2 py-0.5 text-sm hover:bg-muted/50"
              >
                ✕ Close
              </button>
            </div>
            {replayError && <div className="text-sm text-destructive">Replay failed: {replayError}</div>}
            <div ref={playerHost} />
          </div>
        </div>
      )}
    </div>
  );
}
